import {action, makeObservable, observable} from "mobx";
import {TopicConfiguration} from "../../config/topic-configuration";
import {MessdatenConfiguration} from "../../config/messdaten-configuration";
import MatomoTracker from "@datapunt/matomo-tracker-js";
import {NavigateFunction} from "react-router";

import {Authentication} from "../../core/authentication";

// eslint-disable-next-line no-shadow
export enum MessageBoxType {
    Standard = "standard",
    Info = "info",
    Success = "success",
    Warning = "warning",
    Error = "error"
}

export interface MessageBoxProps {
    type?: MessageBoxType;
    title: string;
    message: string;
    subTitle?: string;
    hasProgress?: boolean;
    percent?: number;
    okButton?: boolean;
    cancelButton?: boolean;
}

export class AppState {
    // The currently activated topic
    @observable topic: TopicConfiguration;
    @observable location: Location;
    @observable match = {};
    @observable navigate: NavigateFunction;
    @observable subtopic?: TopicConfiguration;
    @observable subtopicId?: string;
    @observable subtopicLabel?: string;
    @observable objectId?: string;
    @observable objectLabel?: string;

    @observable authentication: Authentication;
    /**
     * If user is logged in to portal
     * is checked at application startup with IdentityManager
     */
    @observable loggedIn: boolean;
    @observable esriCredential: __esri.Credential | null;
    @observable messageBox = {
        type: MessageBoxType.Standard,
        show: false,
        title: "",
        message: "",
        subTitle: "",
        hasProgress: false,
        percent: 0,
        okButton: true,
        cancelButton: false,
    };
    messdatenConfig: MessdatenConfiguration;
    tracker: MatomoTracker | undefined;
    constructor(messdatenConfig: MessdatenConfiguration, tracker?: MatomoTracker) {
        makeObservable(this);
        this.messdatenConfig = messdatenConfig;
        this.tracker = tracker;
    }

    @action setRoute(location: Location, match: object, navigate: NavigateFunction): void {
        this.location = location;
        this.match = match;
        this.navigate = navigate;
        if (this.tracker !== undefined) {
            // TODO: Timeout durch zuverlässigere Lösung ersetzten
            setTimeout(() => {
                if (this.tracker !== undefined) { this.tracker.trackPageView();}
            }, 200);
        }
    }
    @action removeRouteHash(): void {
        if (this.tracker !== undefined) {
            this.tracker.trackPageView();
        }
        this.location.hash = "";
        this.navigate(this.location.pathname);
    }
    @action setRoutePath(pathname: string): void {
        if (this.location.pathname !== pathname) {
            this.navigate(pathname);
            this.location.pathname = pathname;
            if (this.tracker !== undefined) {
                // TODO: Timeout durch zuverlässigere Lösung ersetzten
                setTimeout(() => {
                    if (this.tracker !== undefined) { this.tracker.trackPageView();}
                }, 200);
            }
        }
    }
    getRoute(): string {
        return this.location.pathname;
    }
    @action setTopic(topic: TopicConfiguration): void {
        this.topic = topic;
    }
    @action setTopicById(topicId: string): void {
        const topic = this.messdatenConfig.getTopic(topicId);
        if (topic) {
            this.topic = topic;
        }
    }
    @action setTopicByRoute(route: string): TopicConfiguration | void {
        const topic = this.messdatenConfig.getTopicByRoute(`/${route}`);
        if (topic) {
            this.topic = topic;
        }
        return topic;
    }
    @action setSubtopic(subtopic: TopicConfiguration): void {
        //subtopic.revision = Math.random().toString(); // Neues Subtopic erzwingen
        this.subtopic = subtopic;
        this.subtopicId = subtopic.id;
        this.subtopicLabel = subtopic.label? subtopic.label: subtopic.name;
    }
    @action setSubtopicId(subtopicId: string, subtopicLabel?: string): void {
        this.subtopicId = subtopicId;
        if (typeof subtopicLabel === "string") {
            this.subtopicLabel = subtopicLabel;
        }
    }
    @action setSubtopicLabel(subtopicLabel?: string): void {
        this.subtopicLabel = subtopicLabel;
    }
    @action setObjectId(objectId: string, objectLabel?: string): void {
        this.objectId = objectId;
        if (typeof objectLabel === "string") {
            this.objectLabel = objectLabel;
        }
    }
    @action setObjectLabel(objectLabel?: string): void {
        this.objectLabel = objectLabel;
    }

    @action setAuthentication(authentication: Authentication): void {
        this.authentication = authentication;
    }
    @action setLoggedIn(loggedIn: boolean, credential?: __esri.Credential): void {
        this.loggedIn = loggedIn;
        this.esriCredential = credential ? credential : null;
    }
    /**
     * Show a message box
     *
     * @param props MessageBoxProps
     */
    @action showMessageBox(props: MessageBoxProps): void {
        this.messageBox.show = true;

        const {title, message, subTitle, hasProgress, percent} = props;
        this.messageBox.title = title;
        this.messageBox.message = message;
        if (subTitle) {
            this.messageBox.subTitle = subTitle;
        }
        this.messageBox.hasProgress = hasProgress || false;
        if (percent) {
            this.messageBox.percent = percent;
        }

        if (props.hasOwnProperty("type") && props.type !== undefined) {
            this.messageBox.type = props.type;
        }
        if (props.hasOwnProperty("okButton") && props.okButton !== undefined) {
            this.messageBox.okButton = props.okButton;
        }
        if (props.hasOwnProperty("cancelButton") && props.cancelButton !== undefined) {
            this.messageBox.cancelButton = props.cancelButton;
        }
    }

    /**
     * Close the message box
     */
    @action closeMessageBox(): void {
        this.messageBox.type = MessageBoxType.Standard;
        this.messageBox.show = false;
        this.messageBox.title = "";
        this.messageBox.message = "";
        this.messageBox.subTitle = "";
        this.messageBox.hasProgress = false;
        this.messageBox.percent = 0;
        this.messageBox.okButton = true;
        this.messageBox.cancelButton = false;
    }

    /**
     * Update progress on message box
     * Use 'hasProgress = true' to show progress bar on message box
     *
     * @param percent
     */
    @action progressMessageBox(percent: number): void {
        if (percent < 0) {
            percent = 0;
        }
        if (percent > 100) {
            percent = 100;
        }
        this.messageBox.percent = percent;
    }
}
